/* @img-migrated */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Rss, Plus, Trash2, RefreshCw, ExternalLink, Eye, EyeOff, Star, Loader2,
  CheckCircle2, AlertTriangle, Search, X,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

import Img from '../../components/Img';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtAgo = (iso) => {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'just now';
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

const AdminExternalNews = () => {
  const [feeds, setFeeds] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState('all'); // all | hidden | visible
  const [feedFilter, setFeedFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [addOpen, setAddOpen] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [feedsRes, itemsRes] = await Promise.all([
        axios.get(`${API}/admin/external-news/feeds`, { withCredentials: true }),
        axios.get(`${API}/admin/external-news/items`, { params: { limit: 100 }, withCredentials: true }),
      ]);
      setFeeds(feedsRes.data?.feeds || []);
      setItems(itemsRes.data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const res = await axios.post(`${API}/admin/external-news/refresh`, {}, { withCredentials: true });
      toast.success(`Fetched ${res.data?.total_added ?? 0} new articles`);
      await fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  const updateFeed = async (id, patch) => {
    try {
      await axios.put(`${API}/admin/external-news/feeds/${id}`, patch, { withCredentials: true });
      await fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    }
  };

  const deleteFeed = async (f) => {
    if (!window.confirm(`Delete feed "${f.label}" and all its articles?`)) return;
    try {
      await axios.delete(`${API}/admin/external-news/feeds/${f.id}`, { withCredentials: true });
      toast.success('Feed removed');
      await fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  const moderateItem = async (id, patch) => {
    try {
      await axios.put(`${API}/admin/external-news/items/${id}`, patch, { withCredentials: true });
      setItems((cur) => cur.map((it) => (it.id === id ? { ...it, ...patch } : it)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Moderation failed');
    }
  };

  const filteredItems = items.filter((it) => {
    if (filter === 'hidden' && !it.hidden) return false;
    if (filter === 'visible' && it.hidden) return false;
    if (feedFilter !== 'all' && it.feed_id !== feedFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(it.title || '').toLowerCase().includes(q) && !(it.summary || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto" data-testid="admin-external-news">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
        <div>
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-orange-50 text-orange-700 text-[10px] font-bold uppercase tracking-wider mb-2">
            <Rss className="w-3 h-3" /> External News · RSS
          </div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#002249]">External News Aggregation</h1>
          <p className="text-slate-500 text-sm mt-1 max-w-2xl">
            Pulls economy &amp; business news from Dominican press feeds every 4 hours. Hide individual articles you don&rsquo;t want surfaced.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={fetchAll} className="h-10 rounded-lg" data-testid="ext-reload">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Reload
          </Button>
          <Button onClick={refresh} disabled={refreshing} className="h-10 rounded-lg bg-[#002249] hover:bg-[#0a3570] text-white" data-testid="ext-fetch-now">
            {refreshing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
            Fetch Now
          </Button>
          <Button onClick={() => setAddOpen(true)} className="h-10 rounded-lg bg-[#E63946] hover:bg-[#c02a35] text-white" data-testid="ext-add-feed">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Feed
          </Button>
        </div>
      </div>

      {/* Feeds */}
      <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="ext-feeds">
        <h3 className="text-[13px] font-bold text-[#002249] mb-3">Subscribed feeds</h3>
        {loading ? (
          <div className="space-y-2">{[1,2,3].map((i) => <div key={i} className="h-12 bg-slate-100 animate-pulse rounded-lg" />)}</div>
        ) : feeds.length === 0 ? (
          <p className="text-[12px] text-slate-500">No feeds yet — add one above.</p>
        ) : (
          <div className="space-y-1.5">
            {feeds.map((f) => (
              <div key={f.id} className="flex items-center gap-3 p-2.5 border border-slate-200 rounded-lg" data-testid={`ext-feed-${f.id}`}>
                <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${f.active ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-bold text-[#002249] truncate">{f.label}</p>
                  <p className="text-[11px] text-slate-500 truncate">{f.url}</p>
                  <p className="text-[10.5px] text-slate-400">
                    Last fetch: {fmtAgo(f.last_fetched_at)}
                    {typeof f.last_added === 'number' && ` · ${f.last_added} added, ${f.last_skipped ?? 0} skipped`}
                    {f.last_error && <span className="text-rose-600"> · error: {f.last_error}</span>}
                  </p>
                </div>
                <button
                  onClick={() => updateFeed(f.id, { active: !f.active })}
                  className="h-7 px-2.5 rounded-md text-[11.5px] font-semibold border border-slate-200 hover:bg-slate-50"
                  data-testid={`ext-feed-toggle-${f.id}`}
                >
                  {f.active ? 'Active' : 'Paused'}
                </button>
                <button
                  onClick={() => deleteFeed(f)}
                  className="h-7 w-7 rounded-md text-slate-500 hover:bg-rose-50 hover:text-rose-600 flex items-center justify-center"
                  title="Delete feed"
                  data-testid={`ext-feed-delete-${f.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Items toolbar */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 mt-4 flex flex-col sm:flex-row gap-2 sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search articles..." className="h-9 pl-9 rounded-lg" data-testid="ext-search" />
        </div>
        <select value={feedFilter} onChange={(e) => setFeedFilter(e.target.value)} className="h-9 px-2.5 border border-slate-200 rounded-md text-[12.5px] bg-white" data-testid="ext-filter-feed">
          <option value="all">All feeds</option>
          {feeds.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
        </select>
        <div className="flex items-center gap-1">
          {['all', 'visible', 'hidden'].map((id) => (
            <button
              key={id}
              onClick={() => setFilter(id)}
              data-testid={`ext-filter-${id}`}
              className={`h-9 px-3 rounded-md text-[12px] font-semibold ${filter === id ? 'bg-[#002249] text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}
            >
              {id[0].toUpperCase() + id.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Items list */}
      <div className="mt-4 space-y-2" data-testid="ext-items">
        {filteredItems.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center text-sm text-slate-500">
            No articles match the filters.
          </div>
        ) : filteredItems.map((it) => (
          <div key={it.id} className={`bg-white border rounded-xl p-3 flex gap-3 ${it.hidden ? 'border-slate-200 opacity-60' : 'border-slate-200'}`} data-testid={`ext-item-${it.id}`}>
            {it.image_url && <Img variant="generic" src={it.image_url} alt="" className="w-24 h-16 object-cover rounded-md flex-shrink-0" loading="lazy" />}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600">{it.source_label}</span>
                {it.featured && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700"><Star className="w-3 h-3 fill-amber-500 text-amber-500" /> Featured</span>}
                <span className="text-[10.5px] text-slate-400">{fmtAgo(it.published_at)}</span>
              </div>
              <h4 className="text-[13.5px] font-bold text-[#002249] truncate mt-0.5" title={it.title}>{it.title}</h4>
              <p className="text-[12px] text-slate-500 line-clamp-2">{it.summary}</p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <a href={it.link} target="_blank" rel="noopener noreferrer" className="h-7 w-7 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-500" title="Open original" data-testid={`ext-item-open-${it.id}`}>
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <button onClick={() => moderateItem(it.id, { featured: !it.featured })} className={`h-7 w-7 rounded-md flex items-center justify-center ${it.featured ? 'bg-amber-50 text-amber-600' : 'text-slate-500 hover:bg-amber-50 hover:text-amber-600'}`} title="Toggle featured" data-testid={`ext-item-feature-${it.id}`}>
                <Star className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => moderateItem(it.id, { hidden: !it.hidden })} className={`h-7 w-7 rounded-md flex items-center justify-center ${it.hidden ? 'bg-slate-100 text-slate-600' : 'text-slate-500 hover:bg-slate-100'}`} title="Hide / show" data-testid={`ext-item-hide-${it.id}`}>
                {it.hidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
        ))}
      </div>

      <AnimatePresence>
        {addOpen && <AddFeedModal onClose={() => setAddOpen(false)} onSaved={() => { setAddOpen(false); fetchAll(); }} />}
      </AnimatePresence>
    </div>
  );
};

const AddFeedModal = ({ onClose, onSaved }) => {
  const [form, setForm] = useState({ label: '', url: '', category: 'economy', active: true });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.label.trim() || !form.url.trim()) {
      toast.error('Label and URL are required');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/external-news/feeds`, form, { withCredentials: true });
      toast.success('Feed added');
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not add feed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div className="fixed inset-0 z-[200] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <motion.div initial={{ y: 20, scale: 0.98 }} animate={{ y: 0, scale: 1 }} exit={{ y: 12, scale: 0.98 }}
        className="bg-white w-full max-w-md rounded-xl overflow-hidden" data-testid="ext-feed-modal">
        <div className="px-5 py-3.5 bg-[#002249] text-white flex items-center justify-between">
          <h2 className="text-[15px] font-bold">Add RSS feed</h2>
          <button onClick={onClose} className="h-8 w-8 rounded-md hover:bg-white/10 flex items-center justify-center" data-testid="ext-feed-modal-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <Label>Label</Label>
            <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g., El Caribe · Economía" data-testid="ext-feed-label" maxLength={120} />
          </div>
          <div>
            <Label>RSS URL</Label>
            <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://..." data-testid="ext-feed-url" maxLength={500} />
          </div>
          <div>
            <Label>Category</Label>
            <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="economy" data-testid="ext-feed-category" maxLength={40} />
          </div>
          <label className="inline-flex items-center gap-2 text-[13px] cursor-pointer" data-testid="ext-feed-active-toggle">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            Active
          </label>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-[#E63946] hover:bg-[#c02a35] text-white" data-testid="ext-feed-save">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />} Add
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AdminExternalNews;
